import React, { useState, useRef } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import DoneIcon from '@material-ui/icons/Done';
import { ListItemSecondaryAction, makeStyles, Menu } from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import grey from '@material-ui/core/colors/grey';
import { GET_ALL_CATEGORIES, TOGGLE_DISABLED } from '../gql/categories';
import handleArrayToggle from '../utils/handleArrayToggle';
import CategoryLoading from './CategoryLoading';

const useStyles = makeStyles((theme) => ({
  text: {
    color: '#586069',
  },
  label: {
    color: '#586069',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
    },
    fontSize: '0.875rem',
  },
  widerSecondaryAction: {
    paddingRight: '130px',
  },
  pendingIcon: {
    color: grey[500],
  },
  doneIcon: {
    color: theme.palette.primary.main,
  },
  secondaryAction: {
    marginTop: '4px',
    marginBottom: theme.spacing(1),
  },
}));

const CategoriesItem = ({
  category,
  checked,
  setChecked,
  sortLoading,
  setDeleteCategory,
  setOpenDelete,
}) => {
  const menuRef = useRef(null);
  const matches = useMediaQuery('(min-width:600px)');
  const {
    id,
    folderName,
    displayName,
    desc,
    disabled,
    countPendingFiles,
    sortedFiles: { count },
  } = category;
  const [toggleDisabled] = useMutation(TOGGLE_DISABLED, {
    refetchQueries: [{ query: GET_ALL_CATEGORIES }],
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useStyles();
  const labelId = `category-label-${id}`;

  const handleSwitchToggle = () => {
    toggleDisabled({ variables: { id, input: { disabled: !disabled } } });
  };

  const handleMenuClick = (e) => {
    setAnchorEl(true);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    setDeleteCategory(category);
    setOpenDelete(true);
  };

  return (
    <ListItem
      role={undefined}
      dense
      button
      onClick={() => handleArrayToggle(id, checked, setChecked)}
      classes={{ secondaryAction: classes.widerSecondaryAction }}
    >
      <ListItemIcon>
        <Checkbox
          edge="start"
          color="primary"
          tabIndex={-1}
          disableRipple
          inputProps={{ 'aria-labelledby': labelId }}
          checked={checked.indexOf(id) !== -1}
        />
      </ListItemIcon>
      <ListItemText>
        <Grid container id={labelId} spacing={2}>
          <Grid container item xs={12} md={9} wrap="nowrap">
            <Grid item zeroMinWidth>
              <Typography variant="body1">{displayName}</Typography>
              <Typography variant="body2" className={classes.text} noWrap>
                {desc ? `${folderName} - ${desc}` : `${folderName}`}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            item
            xs={12}
            md={3}
            className={classes.count}
            direction="row"
            alignItems="center"
            spacing={1}
          >
            <Grid item>
              {sortLoading.indexOf(id) < 0 ? (
                <HourglassEmptyIcon className={classes.pendingIcon} />
              ) : (
                <CategoryLoading />
              )}
            </Grid>
            <Grid item>
              <Typography variant="body1">
                {`${countPendingFiles} / ${count}`}
              </Typography>
            </Grid>
            <Grid item>
              <DoneIcon className={classes.doneIcon} />
            </Grid>
          </Grid>
        </Grid>
      </ListItemText>
      <ListItemSecondaryAction className={classes.secondaryAction}>
        <FormGroup row>
          <FormControlLabel
            control={
              <Switch
                checked={!disabled}
                onChange={handleSwitchToggle}
                color="primary"
                size={matches ? 'medium' : 'small'}
              />
            }
            labelPlacement="start"
            classes={{ label: classes.label }}
            label="Active"
          />
          <IconButton
            edge="end"
            aria-label="more"
            ref={menuRef}
            onClick={handleMenuClick}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id={`Menu-${id}`}
            anchorEl={() => menuRef.current}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem component={NavLink} to={`/categories/${id}`}>
              Edit
            </MenuItem>
            <MenuItem onClick={handleDelete}>Delete</MenuItem>
          </Menu>
        </FormGroup>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

CategoriesItem.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  category: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  checked: PropTypes.array.isRequired,
  setChecked: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  sortLoading: PropTypes.array.isRequired,
  setDeleteCategory: PropTypes.func.isRequired,
  setOpenDelete: PropTypes.func.isRequired,
};

export default CategoriesItem;
